import { Link } from "react-router-dom";
import {
  Sparkles,
  Users,
  Compass,
  Rocket,
  Shield,
  Globe,
  ArrowUpRight,
} from "lucide-react";
import PartnerMarquee from "@/components/site/PartnerMarquee";
import CountUp from "@/components/site/CountUp";
import TextMarquee from "@/components/site/TextMarquee";
import { ABOUT_SECTIONS, IMAGES, INDUSTRIES, COMPANY } from "@/constants/siteData";

const NUMBERS = [
  { k: "16+", v: "Enterprise clients", color: "#1E5AF6" },
  { k: "12", v: "Oracle tracks", color: "#16A374" },
  { k: "07", v: "Industries", color: "#0B1220" },
  { k: "24/7", v: "Support model", color: "#1E5AF6" },
];

const PRINCIPLES = [
  {
    Icon: Compass,
    title: "Outcome-led",
    body: "We tie every engagement to measurable business outcomes — not just deliverables.",
    color: "#1E5AF6",
  },
  {
    Icon: Users,
    title: "Collaborative by design",
    body: "Our engagement model builds capabilities in your people while we build the technology.",
    color: "#16A374",
  },
  {
    Icon: Rocket,
    title: "Ready-to-run",
    body: "Reusable accelerators and playbooks so we launch solutions on day one, not week twelve.",
    color: "#0B1220",
  },
  {
    Icon: Sparkles,
    title: "AI-first",
    body: "Every engagement is stress-tested for where AI, GenAI and agentic patterns unlock leverage.",
    color: "#1E5AF6",
    accentTop: true,
  },
  {
    Icon: Shield,
    title: "Enterprise-grade",
    body: "Governance, security and observability are treated as first-class citizens across every build.",
    color: "#16A374",
  },
  {
    Icon: Globe,
    title: "Global footprint",
    body: "Offices in Dubai (Global HQ), Riyadh, New York and Chennai — one team delivering across MENA, Americas and Asia-Pacific.",
    color: "#0B1220",
  },
];

const SECTION_ACCENTS = ["#1E5AF6", "#16A374", "#0B1220"];

export default function AboutPage() {
  return (
    <div data-testid="about-page">
      {/* Colourful hero */}
      <header
        data-testid="about-header"
        className="relative overflow-hidden pt-32 md:pt-40 pb-20 md:pb-28 border-b border-[#e5e7eb]"
      >
        <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute -top-24 -left-16 h-[520px] w-[520px] rounded-full bg-[#1E5AF6]/12 blur-[120px] aurora" />
          <div className="absolute top-20 right-0 h-[520px] w-[520px] rounded-full bg-[#16A374]/12 blur-[120px] aurora" />
        </div>
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.035]"
          style={{
            backgroundImage:
              "linear-gradient(#0B1220 1px, transparent 1px), linear-gradient(90deg, #0B1220 1px, transparent 1px)",
            backgroundSize: "80px 80px",
          }}
        />
        <div className="container-page relative">
          <p className="eyebrow mb-6">
            <span className="inline-block h-2 w-2 mr-1 align-middle bg-gradient-to-br from-[#1E5AF6] to-[#16A374]" />{" "}
            About · DigiTrans Technology
          </p>
          <h1 className="font-display font-extrabold text-5xl sm:text-6xl md:text-7xl leading-[0.95] tracking-tighter max-w-5xl">
            Delivering{" "}
            <span className="grad-text">clarity</span>
            <br />
            in a cluttered business
            <br />
            environment.
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-[#4B5563] leading-relaxed">
            We start by assessing where a company stands — and where it wants to
            go. We look at its people, technology, and data. At its culture,
            leadership, and governance. And at the industry trends and
            disruptions shaping what&apos;s next.
          </p>

          {/* Numbers band */}
          <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-4">
            {NUMBERS.map((n) => (
              <div
                key={n.k}
                className="border border-[#e5e7eb] bg-white p-5 shine box-elevated"
                data-testid={`about-number-${n.k}`}
              >
                <div style={{ color: n.color }}>
                  <CountUp
                    value={n.k}
                    className="font-display text-4xl md:text-5xl font-extrabold tracking-tighter block"
                  />
                </div>
                <p className="text-xs text-[#4B5563] mt-2 leading-snug uppercase tracking-[0.14em] font-mono">
                  {n.v}
                </p>
              </div>
            ))}
          </div>
        </div>
      </header>

      {/* Narrative story sections with coloured labels */}
      <section className="py-24 md:py-32">
        <div className="container-page grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5 md:sticky md:top-32 self-start">
            <div className="relative aspect-[4/5] w-full overflow-hidden grad-border box-elevated">
              <img
                src={IMAGES.team}
                alt="Professional corporate consulting team"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#1E5AF6]/25 via-transparent to-[#16A374]/25 mix-blend-screen" />
              <div className="absolute bottom-5 left-5 right-5 text-white">
                <p className="font-mono text-[10px] tracking-[0.3em] uppercase opacity-80">
                  Advisory · Engineering · Design
                </p>
                <p className="font-display text-xl font-bold mt-2 leading-tight drop-shadow">
                  A team built to shape enterprise futures.
                </p>
              </div>
            </div>
            <div className="mt-6 grid grid-cols-3 gap-0 border border-[#e5e7eb] bg-white">
              {[
                { k: "Advisory", c: "#1E5AF6" },
                { k: "Engineering", c: "#16A374" },
                { k: "Design", c: "#0B1220" },
              ].map((p, i) => (
                <div key={p.k} className={`p-3 ${i < 2 ? "border-r border-[#e5e7eb]" : ""}`}>
                  <span
                    className="inline-block h-1.5 w-6 mb-2"
                    style={{ background: p.c }}
                  />
                  <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-[#0B1220]">
                    {p.k}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="md:col-span-7 space-y-20">
            {ABOUT_SECTIONS.map((s, i) => (
              <article
                key={s.key}
                data-testid={`about-section-${s.key}`}
                className="relative pl-6"
                style={{ borderLeft: `2px solid ${SECTION_ACCENTS[i % SECTION_ACCENTS.length]}` }}
              >
                <p
                  className="font-mono text-[10px] tracking-[0.24em] uppercase mb-3"
                  style={{ color: SECTION_ACCENTS[i % SECTION_ACCENTS.length] }}
                >
                  {s.label}
                </p>
                <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tighter">
                  {s.title}
                </h2>
                <div className="mt-6 space-y-5">
                  {s.body.map((p, idx) => (
                    <p key={idx} className="text-[#374151] leading-relaxed text-lg">
                      {p}
                    </p>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Principles grid */}
      <section className="bg-[#f9fafb] border-y border-[#e5e7eb] py-24 md:py-32">
        <div className="container-page">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-14 items-end">
            <div className="md:col-span-7">
              <p className="eyebrow mb-4">P · Principles</p>
              <h2 className="font-display text-4xl md:text-5xl font-extrabold tracking-tighter leading-[1.05]">
                The principles that
                <br />
                <span className="grad-text">guide every engagement.</span>
              </h2>
            </div>
            <div className="md:col-span-5">
              <p className="text-[#4B5563] text-lg leading-relaxed">
                Six operating principles that shape how we design engagements,
                assemble teams, and deliver outcomes for enterprise clients.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {PRINCIPLES.map(({ Icon, title, body, color, accentTop }, idx) => (
              <div
                key={title}
                data-testid={`about-principle-${idx}`}
                className="group relative shine box-elevated border border-[#e5e7eb] bg-white p-7 min-h-[220px]"
              >
                {accentTop && (
                  <span className="absolute top-0 left-0 h-[3px] w-full bg-gradient-to-r from-[#1E5AF6] via-[#23D89A] to-[#16A374]" />
                )}
                <div
                  className="inline-flex h-11 w-11 items-center justify-center mb-4"
                  style={{ background: `${color}12`, color }}
                >
                  <Icon className="w-5 h-5" strokeWidth={2.25} />
                </div>
                <h3 className="font-display text-xl font-bold mb-2">{title}</h3>
                <p className="text-sm text-[#4B5563] leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Big text marquee — motion band */}
      <section
        data-testid="about-text-marquee"
        className="py-10 md:py-14 border-y border-[#e5e7eb] overflow-hidden bg-white"
      >
        <TextMarquee
          items={[
            "Advisory",
            "Engineering",
            "Design",
            "Data",
            "AI",
            "Agents",
            "Oracle",
            "Fusion",
            "Cloud",
            "Global",
          ]}
          reverse
        />
      </section>

      {/* Bold quote strip */}
      <section
        data-testid="about-quote"
        className="relative overflow-hidden bg-ink-deep text-white"
      >
        <div aria-hidden className="pointer-events-none absolute inset-0">
          <div className="absolute -top-24 left-1/4 h-[420px] w-[420px] rounded-full bg-[#1E5AF6]/25 blur-[110px] aurora" />
          <div className="absolute bottom-0 right-0 h-[420px] w-[420px] rounded-full bg-[#16A374]/25 blur-[110px] aurora" />
        </div>
        <div className="container-page relative py-24 md:py-32 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-8">
            <p className="eyebrow text-[#23D89A] mb-6">Point of view</p>
            <p
              className="font-display text-3xl md:text-4xl lg:text-5xl font-bold leading-tight tracking-tight"
            >
              &ldquo;In today&apos;s dynamic and uncertain business environment,
              organisations must take ownership of their{" "}
              <span className="grad-text">digital future</span> — and do so with
              partners who build capability, not dependency.&rdquo;
            </p>
            <p className="mt-6 font-mono text-xs tracking-[0.24em] uppercase text-white/60">
              DigiTrans Advisory · Operating philosophy
            </p>
          </div>
          <div className="md:col-span-4 md:text-right">
            <Link
              to="/capabilities"
              data-testid="about-cta-capabilities"
              className="group relative shine shine-dark box-elevated-dark inline-flex items-center gap-2 bg-white text-[#0B1220] px-7 py-4 text-sm font-semibold hover:bg-[#1E5AF6] hover:text-white transition-colors"
            >
              See our capabilities
              <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </Link>
            <div className="mt-6">
              <a
                href={`mailto:${COMPANY.email}?subject=Discovery`}
                data-testid="about-cta-connect"
                className="text-sm text-white/60 hover:text-white link-underline"
              >
                Or connect with our team →
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Industries — colored top stripes */}
      <section className="bg-white py-24 md:py-32">
        <div className="container-page">
          <div className="mb-12 grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-7">
              <p className="eyebrow mb-4">I · Industries</p>
              <h2 className="font-display text-4xl md:text-5xl font-extrabold tracking-tighter">
                Sector expertise,
                <br />
                <span className="grad-text">delivered by practitioners.</span>
              </h2>
            </div>
            <div className="md:col-span-5">
              <p className="text-[#4B5563] text-lg leading-relaxed">
                Deep vertical knowledge across the sectors most impacted by
                digital, data and AI disruption.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {INDUSTRIES.map((ind, i) => {
              const stripe = SECTION_ACCENTS[i % SECTION_ACCENTS.length];
              return (
                <article
                  key={ind.key}
                  data-testid={`about-industry-${ind.key}`}
                  className="relative shine box-elevated border border-[#e5e7eb] bg-white p-7 group hover:border-[#0B1220] transition-colors"
                >
                  <span
                    className="absolute top-0 left-0 h-[3px] w-full"
                    style={{ background: stripe }}
                  />
                  <p className="font-mono text-[10px] tracking-[0.24em] uppercase mb-3" style={{ color: stripe }}>
                    S.{String(i + 1).padStart(2, "0")}
                  </p>
                  <h3 className="font-display text-xl font-bold mb-3">{ind.name}</h3>
                  <p className="text-sm text-[#4B5563] leading-relaxed line-clamp-5">
                    {ind.summary}
                  </p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <PartnerMarquee label="Partial listing of customers & partners" />
    </div>
  );
}
